import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Screen, ScreenTitle, ErrorNote, PrimaryButton } from '../ui/Screen.jsx'
import { QuestionnaireBody } from '../items/index.js'
import { useConfigBundle, formatsOf } from '../config.js'
import { day0 as day0Api } from '../api.js'
import { saveAnswer, STALE } from '../answers.js'
import { useQueryClient } from '@tanstack/react-query'
import {
  sectionsFromState,
  answersFromState,
  requiredIdsFromState,
  resumeSectionIndex,
} from '../day0/plan.js'
import { rejectionCode } from '../answers.js'
import { resolveCrisis, mergeOutcome, acknowledgeSafety } from '../safety.js'
import SafetyScreen from './SafetyScreen.jsx'
import { NavBack, NavLeave, NavWordmark } from '../ui/NavBar.jsx'

/**
 * The day-0 battery: demographics -> Block A -> screening -> Block C -> Block B.
 *
 * SAVE AND RESUME IS FREE, and this screen is built so it stays free. Every
 * answered item POSTs on its own, immediately. There is no draft, no
 * localStorage, no "submit questionnaire" step. Closing the tab loses at most
 * the item currently on screen, and resume is a query: ask the server what is
 * answered and carry on from there.
 */
export default function Day0() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: bundle } = useConfigBundle()

  const state = useQuery({
    queryKey: ['day0', 'state'],
    queryFn: day0Api.state,
    staleTime: 0,
  })

  const formats = useMemo(() => formatsOf(bundle), [bundle])

  // The plan is the server's, rendered as given. No gating, ordering or
  // stub-filtering happens on this side — see day0/plan.js.
  const sections = useMemo(() => sectionsFromState(state.data), [state.data])
  const answers = useMemo(() => answersFromState(state.data), [state.data])
  // required is the default; optional is the only declared state. Never inferred.
  const requiredIds = useMemo(
    () => requiredIdsFromState(state.data, sections),
    [state.data, sections],
  )

  const [local, setLocal] = useState({})
  const [sectionIndex, setSectionIndex] = useState(null)
  // True only for the section she stepped BACK into; it lands on its last page.
  const [enteredFromAfter, setEnteredFromAfter] = useState(false)
  const [saveError, setSaveError] = useState(null)
  // DS-2 §3.9. item_id -> the error from its last failed save, so the message
  // sits against the answer it is about instead of under the whole section.
  const [failures, setFailures] = useState({})
  /*
   * The battery's action, reported up by ItemStack and drawn in the ActionBar.
   * AD-3 §3: the primary belongs in the band on every screen, and the back
   * control belongs in the NavBar.
   */
  const [action, setAction] = useState(null)
  /*
   * A safety outcome the participant has not seen yet.
   *
   * While this is set, NOTHING else renders — not the next section, not the
   * completion screen. That is the whole point: what happened was that someone
   * disclosed self-harm and the next thing she saw was "That's everything for
   * today. Thank you." The completion screen is the single worst place to land
   * after that disclosure, because it thanks her and dismisses her.
   */
  const [pendingSafety, setPendingSafety] = useState(null)
  /*
   * A SAFETY OUTCOME IS NEVER HELD. Operator ruling, 2026-08-09, overturning my
   * own reading of DS-12 §3.
   *
   * I had this deferring to the section boundary, so a crisis screen would not
   * replace a battery screen someone was part-way through. It reads like care
   * and it is not: she discloses, closes the tab before pressing Continue, and
   * the outcome dies with the component. No screen, ever. A participant in
   * distress gets nothing, and the safety_events row still says a level-2 event
   * was raised — the database would have shown the feature working.
   *
   * THE RULE, wider than this ticket: if the server says a level, she sees it
   * before she can do anything else. Nothing batches, queues or defers it,
   * however good the flow argument is.
   *
   * DS-12 governs DISMISSAL, not APPEARANCE. It says both levels show the
   * screen and only the way out differs; it never asked for the appearance to
   * move. I traded a safety guarantee for flow and that was not on the table.
   */

  /*
   * ═══════════════════════════════════════════════════════════════════════
   * THE SCREEN FOLLOWS `current_section_id`. IT DOES NOT KEEP ITS OWN IDEA
   * OF WHERE SHE IS.
   *
   * This used to be `if (sectionIndex === null)` — the server's position was
   * read ONCE, at mount, and from then on the screen advanced itself with
   * `index + 1` and never looked again. A local counter that syncs once and
   * then drifts is the closest thing in this file to a second source of
   * truth, and it drifted: one missed advance left the screen permanently
   * one section behind a correct server, on every subsequent screen.
   *
   * It cost a false accusation. I reported it as a server contradiction —
   * "a section advanced past that still lists required_outstanding" —
   * without reading what current_section_id MEANS. The server defines it as
   * the first section holding outstanding items, so `current = PID5` says
   * OLBI has none. Both states I reported were healthy. The screen was the
   * thing that had not moved.
   *
   * WHICH TRANSITIONS ARE WHOSE, since that is the question this turns on:
   *
   *   SERVER-LED   advance, resume, reload, and the 409 re-resume. All of
   *                them are "she has finished this section", which only the
   *                server can decide — it holds required_outstanding.
   *   HERS         Back. And today Back is `page > 0` inside ItemStack: it
   *                moves between PAGES of one section and never between
   *                sections. So there is currently no transition of hers
   *                that changes the section at all.
   *
   * That makes an unconditional sync correct today. It is still written
   * FORWARD-ONLY, for two reasons that both bite:
   *
   *   The optimistic advance below puts her on i+1 before the refetch
   *   lands. An exact sync would yank her back to i and flicker on every
   *   single section boundary.
   *
   *   If a cross-section Back is ever added, forward-only means it degrades
   *   to "she is moved on when the server next advances" rather than
   *   "her Back is undone in the same frame". That is the seam to revisit
   *   if that control ever appears — not a claim that it is handled.
   *
   * Being AHEAD of the server is not corrected here on purpose: the server
   * refuses an answer from a later section, and that path already resets to
   * null below and re-resumes exactly.
   *
   * #56 forbids the client RECOMPUTING the gate. This is the opposite of
   * recomputing — it is obeying the server's own pointer instead of keeping
   * a rival one.
   * ═══════════════════════════════════════════════════════════════════════
   */
  const serverIndex =
    state.isSuccess && sections.length
      ? resumeSectionIndex(sections, state.data?.current_section_id)
      : null

  useEffect(() => {
    if (serverIndex === null) return
    setSectionIndex((own) => (own === null || serverIndex > own ? serverIndex : own))
  }, [serverIndex])

  /*
   * EVERY section change starts at the top - including the SERVER-LED ones.
   * The optimistic onComplete below already scrolled, but most advances are
   * the server moving current_section_id after the last answer lands, and
   * those arrived wherever the previous part's scroll left them. An effect
   * that scrolls is fine under the one-owner rule: it moves the viewport,
   * never the position.
   */
  useEffect(() => {
    document.querySelector('main')?.scrollTo?.({ top: 0 })
    window.scrollTo({ top: 0 })
  }, [sectionIndex])

  const merged = useMemo(() => ({ ...answers, ...local }), [answers, local])

  async function record(item, answer) {
    setLocal((prev) => ({ ...prev, [item.item_id]: answer }))
    setSaveError(null)
    setFailures((prev) => {
      if (!(item.item_id in prev)) return prev
      const next = { ...prev }
      delete next[item.item_id]
      return next
    })
    try {
      const result = await saveAnswer({ item, answer, beepId: null })
      if (result.status === STALE) {
        // Config moved under this item. Drop what we hold and re-ask it.
        setLocal((prev) => {
          const next = { ...prev }
          delete next[item.item_id]
          return next
        })
        setSaveError(
          new Error('This question was updated. Please take a look and answer it again.'),
        )
        return
      }
      // THE SERVER DECIDES whether a rule matched and at what level. This
      // client holds no thresholds and computes no score; it renders what it is
      // sent. The response carries `safety` on every answer to an is_safety
      // item, and day 0 used to drop it on the floor.
      if (result.safety?.level) {
        const content = await resolveCrisis(result.safety.level, bundle)
        if (content) {
          const outcome = {
            ...content,
            rank: Number(result.safety.level) || 0,
            dismissalRequired: result.safety.dismissal_required ?? content.dismissalRequired,
            /*
             * THE ONLY HANDLE ON THE EVENTS THIS ANSWER RAISED (#132), carried
             * to the dismissal so we can record that she was SHOWN the
             * resources and not merely that a rule fired.
             */
            answerIds: result.safety.answer_id ? [result.safety.answer_id] : [],
          }
          /*
           * Shown NOW, on the answer that raised it.
           *
           * Highest level wins if one is somehow already on screen: two
           * disclosures must never resolve to the gentler screen. It never
           * downgrades, and it is never dropped.
           *
           * mergeOutcome keeps the higher SCREEN and both ANSWER IDS. A day-0
           * screen saves several items in flight at once, so a second outcome
           * landing on top of a first is reachable here — and if the suppressed
           * one's id went with it, that answer's safety_events would stay NULL
           * forever, which is indistinguishable from never having been shown.
           */
          setPendingSafety((prev) => mergeOutcome(prev, outcome))
        }
      }

      // required_outstanding is the server's, so it has to be re-read after
      // every save or Continue would stay disabled on a section the
      // participant has just finished — the dead button, one layer up.
      queryClient.invalidateQueries({ queryKey: ['day0', 'state'] })
    } catch (err) {
      // Should be unreachable: the UI cannot offer an item from a later
      // section. If it fires, the client and server have drifted apart again,
      // which is worth surfacing rather than swallowing.
      if (rejectionCode(err) === 'section_out_of_order') {
        const detail = err.body?.detail ?? {}
        setSaveError(
          new Error(
            `This answer was refused because there are unanswered questions in ${
              detail.current_section_title ?? 'an earlier section'
            }. Taking you back there.`,
          ),
        )
        queryClient.invalidateQueries({ queryKey: ['day0', 'state'] })
        setSectionIndex(null) // re-resume from the server's current section
        return
      }
      /*
       * Against the item, not against the section. On day 0 this matters more
       * than on a beep: the no-skip gate reads the SERVER's
       * required_outstanding, so an answer that never landed leaves the item
       * outstanding and Continue refuses — correctly. Without a message on the
       * item itself she would see a control that will not move and an
       * OutstandingNote naming a question she is certain she answered.
       */
      setFailures((prev) => ({ ...prev, [item.item_id]: err }))
      return false
    }
    return true
  }

  /*
   * BEFORE EVERYTHING. Ahead of loading, ahead of the section, and above all
   * ahead of the completion screen — it must not be possible to reach "that's
   * everything for today" with a safety outcome the participant has not seen.
   * Placing this first is the guard; there is no separate flag to forget.
   */
  if (pendingSafety) {
    return (
      <SafetyScreen
        level={pendingSafety.level}
        resources={pendingSafety.resources}
        countryName={pendingSafety.countryName}
        // DS-12 §1.1. Day 0 can only reach levels 1 and 2 today, so this is
        // false in practice — passed anyway, because the rule belongs to the
        // flag and not to the path, and a level-3 day-0 rule would otherwise
        // arrive silently un-displaced.
        dismissalRequired={pendingSafety.dismissalRequired === true}
        /*
         * Back to the section she is part-way through — she has not finished it
         * and under no-skip she still must. Her answers are safe: every item
         * upserts on its own the moment it is chosen, so nothing in the section
         * was riding on this component staying mounted.
         *
         * ItemStack re-lands on the first item still needing an answer rather
         * than on the one that triggered this, so she is not returned to her
         * own disclosure with the answer still showing (DS-1 part 2).
         *
         * SAY SHE SAW IT (#132), then get out of her way. Fire and forget:
         * acknowledgeSafety swallows its own failure, so nothing gates on it,
         * nothing retries and the screen is never put back in front of her.
         */
        onDismiss={() => {
          acknowledgeSafety(pendingSafety.answerIds)
          setPendingSafety(null)
        }}
      />
    )
  }

  if (state.isLoading || !bundle) {
    return (
      <Screen support nav={{ left: <NavLeave to="/" /> }}>
        <ScreenTitle>Getting your questions</ScreenTitle>
        <p className="text-sm text-[var(--ink-700)]">One moment…</p>
      </Screen>
    )
  }

  if (state.isError) {
    return (
      <Screen support nav={{ left: <NavLeave to="/" /> }}>
        <ScreenTitle>We couldn&rsquo;t load your questions</ScreenTitle>
        <ErrorNote error={state.error} />
      </Screen>
    )
  }

  if (!sections.length) {
    return (
      <Screen support nav={{ left: <NavLeave to="/" /> }}>
        <ScreenTitle>Nothing to answer yet</ScreenTitle>
        <p className="text-sm text-[var(--ink-700)]">Please try again shortly.</p>
      </Screen>
    )
  }

  const index = sectionIndex ?? 0

  if (index >= sections.length) {
    return (
      <Screen
        support
        disposition="status"
        nav={{ left: <NavWordmark /> }}
        action={<PrimaryButton onClick={() => navigate('/')}>Done</PrimaryButton>}
      >
        <ScreenTitle>That&rsquo;s everything for today</ScreenTitle>
        {/*
          CFG-41 B1. "at least 12 hours from now" is DELETED: it is the gap rule
          stated outright, on the screen where she has the most attention to
          spend on it, and it is enough to work out when the first one lands.
          `tomorrow` stays — it is inside the after-day-0 exception, a relative
          word and never a clock time. The em dash went with the clause.
        */}
        <p className="text-base leading-relaxed text-[var(--ink-700)]">
          Thank you. Your check-ins start tomorrow, and you&rsquo;ll get a reminder each time.
        </p>
      </Screen>
    )
  }

  const section = sections[index]
  const { questionnaire } = section

  return (
    <Screen
      support
      stop
      nav={{
        /*
         * §5 — Leave on the first screen of the task, Back on every later one.
         * Never conditional on internal pagination: F5's
         * Back-appears-on-screen-2 is what that produces.
         */
        /*
         * LEAVE POINTS AT /settings, NOT AT HOME, and that is not a shortcut.
         *
         * Home routes on server state and redirects into day 0 until the
         * battery is finished, so `Leave -> /` bounces her straight back here.
         * The walk caught it: mid-battery there was no reachable way to act on
         * consent's promise that she can stop at any time, without giving a
         * reason. /settings is the leaf that holds signing out and leaving the
         * study, and Home's gate does not apply to it.
         *
         * Within the battery the left slot is Back and pages within the
         * section; the way OUT is the footnote row's Stop for now, which is
         * unconditional and on every screen.
         */
        left:
          action?.canGoBack ? (
            <NavBack onBack={action.goBack} />
          ) : index > 0 ? (
            // The first screen of a part: Back is the previous part, on its
            // last screen, answers editable. The server accepts answers to any
            // earlier section (only LATER ones are refused), so this is hers
            // alone; the forward-only sync above leaves her there until she
            // presses Continue at the end of that part.
            <NavBack
              onBack={() => {
                setEnteredFromAfter(true)
                setSectionIndex(index - 1)
                window.scrollTo({ top: 0 })
              }}
            />
          ) : (
            <NavLeave to="/settings" />
          ),
        right:
          typeof state.data?.required_count === 'number'
            ? `${state.data.answered_count ?? 0} of ${state.data.required_count}`
            : null,
      }}
      notice={action?.notice ?? null}
      action={
        action ? (
          <PrimaryButton onClick={action.press} incomplete={action.incomplete}>
            {action.label}
          </PrimaryButton>
        ) : null
      }
    >
      {/*
        THE INSTRUMENT'S NAME IS NEVER SHOWN TO A PARTICIPANT.
        This heading used to read "Patient Health Questionnaire-9", and
        elsewhere "PTSD Checklist for DSM-5" or "Edinburgh Postnatal Depression
        Scale". In an app that deliberately shows no score, no band and no
        interpretation, the section heading was walking the clinical framing
        straight back in and telling someone they are a patient completing a
        PTSD checklist. Titles stay in config untouched, for the loader, the
        export and analysis; they simply do not reach the person answering.

        The heading is now the participant's POSITION. That is derived from the
        server's plan rather than invented copy, it keeps a real <h1> — an empty
        one is the accessibility defect already fixed once on consent — and it
        answers the question the title was accidentally answering: have I moved,
        or am I stuck on an endless page.

        The instruction below carries everything needed to answer, and it is
        already neutral and self-explanatory.
      */}
      <ScreenTitle subtitle={questionnaire.timeframe || undefined}>
        Part {index + 1} of {sections.length}
      </ScreenTitle>

      {/*
        THE PROGRESS RULE IS DELETED (AD-3 §4.4). It is a COUNT in the NavBar
        now and never a bar: at 4 of 221 a 3px rule renders as a 6px blue tick
        that reads as a rendering fault rather than as progress.
      */}
      {/* Identifies which section is on screen without naming it, now that the
          title is gone. The QA walk reads this instead of the heading. */}
      <div data-section-id={questionnaire.id}>
      <QuestionnaireBody
        key={questionnaire.id}
        questionnaire={questionnaire}
        items={section.items}
        formats={formats}
        answers={merged}
        onAnswer={record}
        // Both come from the server. requiredIds says which items count;
        // sectionOutstanding is the gate itself and is never recomputed here.
        requiredIds={requiredIds}
        sectionOutstanding={section.requiredOutstanding}
        timeframe={questionnaire.timeframe}
        // The ScreenTitle above already shows it on every page of this part.
        timeframeInTitle
        failures={failures}
        onRetry={(item) => record(item, merged[item.item_id])}
        /*
         * §5 — `Continue` never becomes `Done` mid-task. ItemStack's
         * `finishLabel` is the label for the last page of THIS SECTION, and
         * day 0 has seventeen of them; leaving it as `Done` made the button
         * swap every time she crossed a part boundary she cannot even see.
         * Only the last section of the battery is an ending.
         */
        finishLabel={index === sections.length - 1 ? 'Done' : 'Continue'}
        startAtEnd={enteredFromAfter}
        onAction={setAction}
        /*
         * OPTIMISTIC, AND RECONCILED ABOVE. She moves the instant she presses,
         * because waiting for a round-trip to change the screen is the kind of
         * lag that makes people press twice. The forward-only sync then puts
         * her wherever the server actually says, so this is a head start and
         * never a second opinion.
         */
        onComplete={() => {
          setEnteredFromAfter(false)
          setSectionIndex(index + 1)
          window.scrollTo({ top: 0 })
        }}
      />
      </div>

      <ErrorNote error={saveError} />

      {/*
        The way out has to be reachable from HERE, not only from the home
        screen. Home redirects into day 0 until the battery is finished, so
        during a 221-item first session — which is exactly when someone might
        decide this is not for them — Settings would otherwise be unreachable
        and the consent promise unactionable.

        Quiet and at the bottom: findable if looked for, not an invitation.
      */}
    </Screen>
  )
}
